import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

interface DataChangeInfo {
  pageName: string;
  timestamp: number;
}

interface DataChangeContextType {
  dataChangeInfo: DataChangeInfo | null;
  notifyDataChange: (pageName: string) => void;
  clearDataChange: () => void;
  ignoreNextChange: (pageName: string, durationMs?: number) => void;
}

const DataChangeContext = createContext<DataChangeContextType | undefined>(undefined);

export const DataChangeProvider = ({ children }: { children: ReactNode }) => {
  const [dataChangeInfo, setDataChangeInfo] = useState<DataChangeInfo | null>(null);
  const ignoredPagesRef = useRef<Map<string, number>>(new Map()); // pageName -> timestamp until which to ignore

  const notifyDataChange = useCallback((pageName: string) => {
    // Check if this page is currently being ignored
    const ignoreUntil = ignoredPagesRef.current.get(pageName);
    const now = Date.now();
    
    if (import.meta.env.DEV) {
      console.log(`[DataChangeContext] notifyDataChange called for ${pageName}:`, {
        ignoreUntil,
        now,
        isIgnored: ignoreUntil && now < ignoreUntil,
      });
    }
    
    if (ignoreUntil && now < ignoreUntil) {
      if (import.meta.env.DEV) {
        console.log(`[DataChangeContext] Ignoring change for ${pageName} (ignored until ${new Date(ignoreUntil).toISOString()})`);
      }
      return; // Ignore this change
    }

    if (import.meta.env.DEV) {
      console.log(`[DataChangeContext] Setting data change info for ${pageName}`);
    }

    setDataChangeInfo({
      pageName,
      timestamp: now,
    });
  }, []);

  const clearDataChange = useCallback(() => {
    setDataChangeInfo(null);
  }, []);

  const ignoreNextChange = useCallback((pageName: string, durationMs = 10000) => {
    // Ignore changes for this page for the specified duration
    ignoredPagesRef.current.set(pageName, Date.now() + durationMs);
  }, []);

  return (
    <DataChangeContext.Provider
      value={{
        dataChangeInfo,
        notifyDataChange,
        clearDataChange,
        ignoreNextChange,
      }}
    >
      {children}
    </DataChangeContext.Provider>
  );
};

export const useDataChange = () => {
  const context = useContext(DataChangeContext);
  if (context === undefined) {
    throw new Error('useDataChange must be used within DataChangeProvider');
  }
  return context;
};

